// Kostenverlauf — eine Linie je App oder je Modell über die Zeit.
//
// Zwei Wahlen, sonst nichts: oben „Apps" oder „Modelle" (was eine Linie ist),
// darunter der Zeitraum „Woche / Monat / Jahr". Gezeigt wird immer die ganze
// Plattform. Bis 10.09.2026 gab es zusätzlich einen Tab je App, vier Zeitkörner und
// einen Reihenumschalter — acht Knöpfe für eine Frage, die zwei beantworten. Und
// davor gruppierte Säulen: bei 30 Tagen und fünf Modellen 150 Säulchen, in denen
// sich der Verlauf EINES Modells nur erraten ließ. Eine Linie zeigt genau den.
//
// Die Zeitachse ist lückenlos: Eimer ohne Kosten stehen als Null da, statt zu fehlen.
// Eine Linie über ausgelassene Tage hinweg behauptete sonst einen Verlauf, den es nie
// gab (07.09. läge direkt neben 27.08.).
//
// Gebucketet wird im Browser: der Endpunkt liefert Tagespunkte (Datum, App, Modell,
// USD), und aus denen lassen sich alle vier Körner rechnen, ohne bei jedem Umschalten
// neu zu laden. Die Datenmenge ist dafür klein genug (Tage × Apps × Modelle).
//
// Farben aus dem Mednos-Design statt einer fremden Palette: Schwarz/Weiss plus je
// App ein Akzent. Study-Lila und Papers-Petrol, dazu Bernstein und Ton-Rot aus
// Studys Kalender — jeweils auf den dunklen Grund der Kosten-Box (#1a1a1a) gleich
// hell nachgezogen, sonst stuende das blasse Petrol neben dem satten Lila als
// Nebensache da. Am 10.09.2026 mit dem Paletten-Validator ueber ALLE Paare geprueft
// (Linien liegen alle zugleich im Bild, nicht nur Nachbarn): bestanden, mit einer
// CVD-Warnung Gold/Rot, die Legende, Tooltip und Tabelle als Zweitkanal abdecken.
// Ein fuenfter Farbton fiel in jeder Variante durch (Blau gegen Lila, Magenta gegen
// Rot, Gruen gegen Gold) — die fuenfte Reihe bekommt deshalb das neutrale Grau.

import { authApi, ppHint } from './platform-api.js';

/** Design-Toene, auf den dunklen Grund nachgezogen, in fester Reihenfolge. */
const SERIENFARBEN = [
  '#9a7cf0', // Study-Lila
  '#21a08f', // Papers-Petrol
  '#b08a1a', // Bernstein
  '#d25a5a', // Ton-Rot
];

/** Neutrales Mednos-Grau (Radiologys Akzent) — fuenfte Reihe bzw. „Andere". */
const NEUTRAL = '#8b8d98';

/** Als App traegt jede ihren Akzent, unabhaengig davon, wie viel sie kostet. */
const APP_FARBEN = { study: '#9a7cf0', papers: '#21a08f' };

/** Ab der sechsten Reihe wird gesammelt statt eine Farbe zu erfinden. */
const REST = 'Andere';

/** Grund der Kosten-Box — Ring um die Punkte, damit sie sich von kreuzenden Linien abheben. */
const FLAECHE = '#1a1a1a';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Zeitraum → Korn und Punktzahl. Das Jahr in Wochen: in Monaten waeren es heute zwei Punkte. */
const ZEITRAEUME = [
  { id: 'woche', titel: 'Woche', korn: 'tag', gruppen: 7 },
  { id: 'monat', titel: 'Monat', korn: 'tag', gruppen: 30 },
  { id: 'jahr', titel: 'Jahr', korn: 'woche', gruppen: 52 },
];

const DIMENSIONEN = [
  { id: 'app', titel: 'Apps' },
  { id: 'modell', titel: 'Modelle' },
];

export const VERLAUF_CSS = `
.mednos-verlauf { display: flex; flex-direction: column; gap: 12px; }
/* Zwei Reihen: oben Apps/Modelle mit der Summe rechts, darunter der Zeitraum. */
.mednos-verlauf__leiste { display: flex; flex-direction: column; gap: 8px; }
.mednos-verlauf__reihe { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
.mednos-verlauf__wahl { display: flex; gap: 2px; }
.mednos-verlauf__knopf {
  padding: 4px 10px; border: 1px solid transparent; border-radius: 999px;
  background: transparent; color: #b0b0b0; cursor: pointer;
  font-family: inherit; font-size: 0.75rem;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}
.mednos-verlauf__knopf:hover { color: #fff; }
.mednos-verlauf__knopf.is-aktiv {
  color: #fff; background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}
.mednos-verlauf__summe { margin-left: auto; font-size: 0.78rem; color: #b0b0b0; }
.mednos-verlauf__summe b { color: #fff; font-size: 0.95rem; font-weight: 600; }
.mednos-verlauf__flaeche { position: relative; }
.mednos-verlauf__svg { display: block; width: 100%; height: auto; }
.mednos-verlauf__svg:focus { outline: none; }
.mednos-verlauf__svg:focus-visible {
  outline: 1px solid rgba(255, 255, 255, 0.3); outline-offset: 2px; border-radius: 4px;
}
/* Zurückhaltendes Gitter — es soll ablesbar machen, nicht auffallen. */
.mednos-verlauf__gitter { stroke: rgba(255, 255, 255, 0.08); stroke-width: 1; }
.mednos-verlauf__achse {
  fill: #b0b0b0; font-size: 9px; font-family: inherit; font-variant-numeric: tabular-nums;
}
.mednos-verlauf__linie {
  fill: none; stroke-width: 2; stroke-linejoin: round; stroke-linecap: round;
}
.mednos-verlauf__kreuz { stroke: rgba(255, 255, 255, 0.35); stroke-width: 1; }
.mednos-verlauf__legende { display: flex; flex-wrap: wrap; gap: 4px 14px; }
.mednos-verlauf__eintrag {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.72rem; color: #b0b0b0;
}
/* Legende und Tooltip spiegeln die Marke: ein kurzer Linienstrich, kein Kästchen. */
.mednos-verlauf__schluessel { width: 12px; height: 2px; border-radius: 1px; flex-shrink: 0; }
.mednos-verlauf__tipp {
  position: absolute; top: 4px; pointer-events: none; z-index: 5;
  display: flex; flex-direction: column; gap: 3px;
  padding: 6px 9px; border-radius: 8px;
  background: #000; border: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 0.72rem; color: #fff; white-space: nowrap;
  opacity: 0; transition: opacity 0.1s;
}
.mednos-verlauf__tipp.is-sichtbar { opacity: 1; }
.mednos-verlauf__tipp-kopf { color: #b0b0b0; margin-bottom: 2px; }
.mednos-verlauf__tipp-zeile { display: flex; align-items: center; gap: 6px; }
.mednos-verlauf__tipp-wert { min-width: 4.4em; font-weight: 600; font-variant-numeric: tabular-nums; }
.mednos-verlauf__tipp-name { color: #b0b0b0; }
.mednos-verlauf__tabelle {
  width: 100%; border-collapse: collapse; font-size: 0.72rem; color: #b0b0b0;
}
.mednos-verlauf__tabelle th, .mednos-verlauf__tabelle td {
  text-align: left; padding: 3px 8px 3px 0; font-weight: 400;
}
.mednos-verlauf__tabelle th { color: #fff; }
.mednos-verlauf__tabelle td.zahl, .mednos-verlauf__tabelle th.zahl {
  text-align: right; font-variant-numeric: tabular-nums;
}
.mednos-verlauf__aufklapp {
  align-self: flex-start; border: none; background: transparent; padding: 0;
  color: #b0b0b0; text-decoration: underline; cursor: pointer;
  font-family: inherit; font-size: 0.72rem;
}
.mednos-verlauf__aufklapp:hover { color: #fff; }
`;

/** USD so, dass Cent-Betraege nicht zu 0,00 werden. */
function usd(betrag) {
  const b = Number(betrag) || 0;
  if (b === 0) return '0 $';
  if (b < 0.01) return `${b.toFixed(4)} $`;
  return `${b.toFixed(2)} $`;
}

/** Montag der Woche, in der `d` liegt. */
function wochenStart(d) {
  const k = new Date(d.getTime());
  // getUTCDay: 0 = Sonntag. Der Montag davor ist also 6 Tage zurueck, nicht 1.
  const versatz = (k.getUTCDay() + 6) % 7;
  k.setUTCDate(k.getUTCDate() - versatz);
  return k;
}

/**
 * Ordnet ein Tagesdatum seinem Eimer zu. Pur und exportiert, damit die
 * Wochengrenze testbar ist.
 */
export function eimerFuer(datum, korn) {
  const d = new Date(`${datum}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  if (korn === 'jahr') return String(d.getUTCFullYear());
  if (korn === 'monat') return datum.slice(0, 7);
  if (korn === 'woche') return wochenStart(d).toISOString().slice(0, 10);
  return datum;
}

/** Der Eimer nach `schluessel` im selben Korn. */
function naechsterEimer(schluessel, korn) {
  if (korn === 'jahr') return String(Number(schluessel) + 1);
  if (korn === 'monat') {
    const [j, m] = schluessel.split('-').map(Number);
    return m === 12 ? `${j + 1}-01` : `${j}-${String(m + 1).padStart(2, '0')}`;
  }
  const d = new Date(`${schluessel}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + (korn === 'woche' ? 7 : 1));
  return d.toISOString().slice(0, 10);
}

/**
 * Alle Eimer von `von` bis `bis` einschliesslich, lueckenlos. Pur und exportiert:
 * hier entscheidet sich, ob ein kostenloser Tag als Null dasteht oder fehlt.
 * Die Schluessel eines Korns haben feste Breite, der Stringvergleich ordnet also
 * chronologisch.
 */
export function eimerFolge(von, bis, korn) {
  const folge = [];
  // Obergrenze gegen eine Endlosschleife bei kaputtem Schluessel; der Endpunkt
  // liefert hoechstens ein Jahr Tage.
  for (let e = von; e <= bis && folge.length < 1000; e = naechsterEimer(e, korn)) folge.push(e);
  return folge;
}

/** Beschriftung eines Eimers auf der Achse. */
function eimerLabel(schluessel, korn) {
  if (korn === 'jahr') return schluessel;
  if (korn === 'monat') return schluessel.slice(2); // 26-08
  const [, m, t] = schluessel.split('-');
  return `${t}.${m}.`;
}

/**
 * Wertachse auf runden Zahlen: Schrittweite 1 / 2 / 2,5 / 5 × 10ⁿ, etwa drei
 * Schritte bis zur Obergrenze. Exportiert, weil „rund" hier eine Regel ist.
 */
export function wertSkala(max) {
  if (!(max > 0)) return { oben: 1, schritt: 0.5 };
  const roh = max / 3;
  const p = 10 ** Math.floor(Math.log10(roh));
  const n = roh / p;
  const f = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  const schritt = f * p;
  return { oben: Math.ceil(max / schritt - 1e-9) * schritt, schritt };
}

function reiheVon(p, dimension) {
  return dimension === 'app' ? (p.app || '(ohne)') : p.modell;
}

/**
 * Feste Farbe je Reihe. Als App tragen Study und Papers ihren Akzent; alle
 * anderen Reihen bekommen die freien Toene nach der Rangfolge ueber ALLE Punkte —
 * nicht nach dem gezeigten Zeitraum, sonst wechselte eine Farbe beim Umschalten.
 * Passen alle Reihen in die freien Toene plus das Grau, bekommt jede ihre Farbe;
 * sonst behalten die ersten ihren Ton, und der Rest teilt sich „Andere" in Grau.
 *
 * @returns {Map<string, string>} Reihenname → Farbe; fehlt ein Name, gehoert er zu „Andere".
 */
function farbZuordnung(punkte, dimension) {
  const summe = new Map();
  for (const p of punkte) {
    const r = reiheVon(p, dimension);
    summe.set(r, (summe.get(r) || 0) + (Number(p.usd) || 0));
  }
  const sortiert = [...summe.entries()].sort((a, b) => b[1] - a[1]).map(([r]) => r);

  const farben = new Map();
  const frei = [...SERIENFARBEN];
  if (dimension === 'app') {
    for (const r of sortiert) {
      const akzent = APP_FARBEN[r.toLowerCase()];
      if (!akzent) continue;
      farben.set(r, akzent);
      frei.splice(frei.indexOf(akzent), 1);
    }
  }
  const uebrig = sortiert.filter((r) => !farben.has(r));
  const benannt = uebrig.length <= frei.length + 1 ? uebrig : uebrig.slice(0, frei.length);
  benannt.forEach((r, i) => farben.set(r, i < frei.length ? frei[i] : NEUTRAL));
  return farben;
}

/**
 * Baut aus den Rohpunkten die Linien fuer ein Zeitkorn und eine Reihenart
 * (Modell oder App), immer ueber die ganze Plattform.
 *
 * Exportiert, weil hier die Regeln stecken, die man nachlesen koennen soll: die
 * Zeitachse beginnt beim ersten Eimer mit Kosten, laeuft lueckenlos bis heute und
 * behaelt davon die letzten `maxGruppen`; Reihen ohne Betrag im gezeigten Fenster
 * fallen weg.
 *
 * @returns {{ gruppen: Array<{ eimer: string, label: string, werte: Map<string, number> }>,
 *             reihen: Array<{ name: string, farbe: string }>, summe: number }}
 */
export function baueSerien(punkte, {
  korn = 'tag', maxGruppen = 30, dimension = 'app', heute = new Date(),
} = {}) {
  const farben = farbZuordnung(punkte, dimension);

  const proEimer = new Map();
  for (const p of punkte) {
    const eimer = eimerFuer(p.datum, korn);
    if (!eimer) continue;
    const wert = Number(p.usd) || 0;
    if (wert <= 0) continue;
    const roh = reiheVon(p, dimension);
    const reihe = farben.has(roh) ? roh : REST;
    if (!proEimer.has(eimer)) proEimer.set(eimer, new Map());
    const m = proEimer.get(eimer);
    m.set(reihe, (m.get(reihe) || 0) + wert);
  }
  const leer = { gruppen: [], reihen: [], summe: 0 };
  if (!proEimer.size) return leer;

  const vorhanden = [...proEimer.keys()].sort();
  const letzter = vorhanden[vorhanden.length - 1];
  const heuteEimer = eimerFuer(heute.toISOString().slice(0, 10), korn);
  const bis = heuteEimer && heuteEimer > letzter ? heuteEimer : letzter;
  const gruppen = eimerFolge(vorhanden[0], bis, korn)
    .slice(-maxGruppen)
    .map((e) => ({ eimer: e, label: eimerLabel(e, korn), werte: proEimer.get(e) || new Map() }));

  const imBild = new Map();
  for (const g of gruppen) {
    for (const [r, w] of g.werte) imBild.set(r, (imBild.get(r) || 0) + w);
  }
  if (!imBild.size) return leer;

  // Reihenfolge in Legende und Tabelle nach Betrag im Bild — die Farbe haengt davon
  // nicht ab. „Andere" steht immer am Ende, auch wenn die Sammlung mehr kostet als
  // ein einzelnes Modell davor.
  const reihen = [...imBild.entries()]
    .sort((a, b) => (a[0] === REST) - (b[0] === REST) || b[1] - a[1])
    .map(([name]) => ({ name, farbe: farben.get(name) || NEUTRAL }));
  const summe = [...imBild.values()].reduce((a, b) => a + b, 0);
  return { gruppen, reihen, summe };
}

function svgEl(name, attrs) {
  const e = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

/** Punkt mit 8 px Fuellung und 2 px Ring in Flaechenfarbe. */
function punktEl(x, y, farbe) {
  return svgEl('circle', { cx: x, cy: y, r: 5, fill: farbe, stroke: FLAECHE, 'stroke-width': 2 });
}

function schluesselEl(farbe) {
  const s = document.createElement('span');
  s.className = 'mednos-verlauf__schluessel';
  s.style.background = farbe;
  return s;
}

/** Tooltip-Inhalt: alle Reihen an diesem Zeitpunkt, Wert vorn, Name dahinter. */
function fuelleTipp(tipp, g, reihen) {
  const kopf = document.createElement('div');
  kopf.className = 'mednos-verlauf__tipp-kopf';
  const summe = reihen.reduce((s, r) => s + (g.werte.get(r.name) || 0), 0);
  kopf.textContent = `${g.label} · ${usd(summe)}`;
  const zeilen = [...reihen]
    .sort((a, b) => (g.werte.get(b.name) || 0) - (g.werte.get(a.name) || 0))
    .map((r) => {
      const zeile = document.createElement('div');
      zeile.className = 'mednos-verlauf__tipp-zeile';
      const wert = document.createElement('span');
      wert.className = 'mednos-verlauf__tipp-wert';
      wert.textContent = usd(g.werte.get(r.name) || 0);
      const name = document.createElement('span');
      name.className = 'mednos-verlauf__tipp-name';
      name.textContent = r.name;
      zeile.append(schluesselEl(r.farbe), wert, name);
      return zeile;
    });
  tipp.replaceChildren(kopf, ...zeilen);
}

/** Zeichnet die Linien als SVG, mit Fadenkreuz fuer Maus und Pfeiltasten. */
function zeichne(flaeche, serien, tipp) {
  const B = 720;
  const H = 210;
  const LINKS = 48;
  const RECHTS = 16;
  const OBEN = 10;
  const UNTEN = 22;
  const plotB = B - LINKS - RECHTS;
  const plotH = H - OBEN - UNTEN;

  const { gruppen, reihen } = serien;
  const n = gruppen.length;
  const max = Math.max(0, ...gruppen.flatMap((g) => [...g.werte.values()]));
  const { oben, schritt } = wertSkala(max);
  const xVon = (i) => (n === 1 ? LINKS + plotB / 2 : LINKS + (plotB * i) / (n - 1));
  const yVon = (w) => OBEN + plotH - (w / oben) * plotH;

  const svg = svgEl('svg', {
    viewBox: `0 0 ${B} ${H}`,
    class: 'mednos-verlauf__svg',
    role: 'img',
    tabindex: '0',
    'aria-label':
      `Kostenverlauf, ${reihen.length === 1 ? 'eine Reihe' : `${reihen.length} Reihen`}, `
      + `${gruppen[0].label} bis ${gruppen[n - 1].label}. Pfeiltasten waehlen einen Zeitpunkt.`,
  });

  // Gitter und Wertachse auf runden Schritten.
  const stufen = Math.round(oben / schritt);
  for (let k = 0; k <= stufen; k++) {
    const w = k * schritt;
    const y = yVon(w);
    svg.append(svgEl('line', {
      x1: LINKS, y1: y, x2: B - RECHTS, y2: y, class: 'mednos-verlauf__gitter',
    }));
    const t = svgEl('text', {
      x: LINKS - 6, y: y + 3, 'text-anchor': 'end', class: 'mednos-verlauf__achse',
    });
    t.textContent = usd(w);
    svg.append(t);
  }

  // Nicht jeden Zeitpunkt beschriften — bei 30 Tagen ueberlappten die Texte. Vom
  // juengsten aus gezaehlt, damit „heute" immer dasteht.
  const jede = Math.ceil(n / 8);
  gruppen.forEach((g, i) => {
    if ((n - 1 - i) % jede !== 0) return;
    const t = svgEl('text', {
      x: xVon(i), y: H - 6, 'text-anchor': 'middle', class: 'mednos-verlauf__achse',
    });
    t.textContent = g.label;
    svg.append(t);
  });

  // Die teuerste Reihe zuletzt, damit sie oben liegt, wo Linien sich kreuzen.
  for (const reihe of [...reihen].reverse()) {
    const pts = gruppen.map((g, i) => [xVon(i), yVon(g.werte.get(reihe.name) || 0)]);
    if (n > 1) {
      svg.append(svgEl('path', {
        d: pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(''),
        stroke: reihe.farbe,
        class: 'mednos-verlauf__linie',
      }));
    }
    // Endpunkt: der juengste Wert. Bei nur einem Zeitpunkt ist er die ganze Reihe.
    const [x, y] = pts[n - 1];
    svg.append(punktEl(x, y, reihe.farbe));
  }

  // Fadenkreuz: die senkrechte Linie findet den Zeitpunkt, die Punkte markieren
  // jede Reihe dort. Leser zielen auf ein Datum, nicht auf eine 2-px-Linie.
  const kreuz = svgEl('line', {
    y1: OBEN, y2: OBEN + plotH, class: 'mednos-verlauf__kreuz', visibility: 'hidden',
  });
  const marken = svgEl('g', { visibility: 'hidden' });
  const markeJeReihe = reihen.map((r) => {
    const c = punktEl(0, 0, r.farbe);
    marken.append(c);
    return c;
  });
  // Fangflaeche ueber dem ganzen Plot, etwas breiter als die Achse, damit der
  // erste und letzte Zeitpunkt nicht auf den Pixel getroffen werden muessen.
  const fang = svgEl('rect', {
    x: LINKS - 12, y: OBEN, width: plotB + 24, height: plotH, fill: 'transparent',
  });
  svg.append(kreuz, marken, fang);

  let aktiv = -1;
  function zeige(i) {
    aktiv = i;
    const x = xVon(i);
    kreuz.setAttribute('x1', x);
    kreuz.setAttribute('x2', x);
    kreuz.setAttribute('visibility', 'visible');
    marken.setAttribute('visibility', 'visible');
    const g = gruppen[i];
    reihen.forEach((r, k) => {
      markeJeReihe[k].setAttribute('cx', x);
      markeJeReihe[k].setAttribute('cy', yVon(g.werte.get(r.name) || 0));
    });
    fuelleTipp(tipp, g, reihen);
    // Rechts der Mitte klappt der Tipp nach links, sonst ragte er aus der Box.
    const anteil = x / B;
    tipp.style.left = `${anteil * 100}%`;
    tipp.style.transform = anteil > 0.55 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)';
    tipp.classList.add('is-sichtbar');
  }
  function verberge() {
    aktiv = -1;
    kreuz.setAttribute('visibility', 'hidden');
    marken.setAttribute('visibility', 'hidden');
    tipp.classList.remove('is-sichtbar');
  }

  fang.addEventListener('pointermove', (e) => {
    const r = svg.getBoundingClientRect();
    const vx = ((e.clientX - r.left) / r.width) * B;
    const i = n === 1 ? 0 : Math.round(((vx - LINKS) / plotB) * (n - 1));
    zeige(Math.min(n - 1, Math.max(0, i)));
  });
  fang.addEventListener('pointerleave', verberge);
  svg.addEventListener('focus', () => { if (aktiv < 0) zeige(n - 1); });
  svg.addEventListener('blur', verberge);
  svg.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const ziel = aktiv < 0 ? n - 1 : aktiv + (e.key === 'ArrowRight' ? 1 : -1);
    zeige(Math.min(n - 1, Math.max(0, ziel)));
  });

  flaeche.replaceChildren(svg, tipp);
}

/** Legende — bei zwei und mehr Reihen immer da, damit Identitaet nie nur Farbe ist. */
function zeichneLegende(el, reihen) {
  el.replaceChildren();
  if (reihen.length < 2) return;
  for (const r of reihen) {
    const eintrag = document.createElement('span');
    eintrag.className = 'mednos-verlauf__eintrag';
    const name = document.createElement('span');
    name.textContent = r.name;
    eintrag.append(schluesselEl(r.farbe), name);
    el.append(eintrag);
  }
}

/** Tabellenansicht — dieselben Zahlen ohne Farbe, fuer Screenreader und zum Nachlesen. */
function zeichneTabelle(el, serien) {
  const tabelle = document.createElement('table');
  tabelle.className = 'mednos-verlauf__tabelle';
  const kopf = document.createElement('tr');
  const erste = document.createElement('th');
  erste.textContent = 'Zeitraum';
  kopf.append(erste);
  for (const r of serien.reihen) {
    const th = document.createElement('th');
    th.className = 'zahl';
    th.textContent = r.name;
    kopf.append(th);
  }
  tabelle.append(kopf);
  for (const g of serien.gruppen) {
    const tr = document.createElement('tr');
    const td = document.createElement('td');
    td.textContent = g.label;
    tr.append(td);
    for (const r of serien.reihen) {
      const z = document.createElement('td');
      z.className = 'zahl';
      const w = g.werte.get(r.name) || 0;
      z.textContent = w > 0 ? usd(w) : '–';
      tr.append(z);
    }
    tabelle.append(tr);
  }
  el.replaceChildren(tabelle);
}

function knopfLeiste(eintraege, aktiv, beiWahl) {
  const leiste = document.createElement('div');
  leiste.className = 'mednos-verlauf__wahl';
  for (const e of eintraege) {
    const k = document.createElement('button');
    k.type = 'button';
    k.className = 'mednos-verlauf__knopf';
    k.textContent = e.titel;
    if (e.id === aktiv) k.classList.add('is-aktiv');
    k.addEventListener('click', () => beiWahl(e.id));
    leiste.append(k);
  }
  return leiste;
}

/**
 * Baut das Verlaufsdiagramm in `container` und laedt die Daten.
 *
 * Es wird EINMAL geladen (ein Jahr Tagespunkte); Tab-, Korn- und Reihenwechsel
 * rechnen nur neu. Ein Nachladen je Umschalten waere spuerbar traege und braechte
 * nichts — die Punkte sind dieselben.
 */
export function renderVerlaufChart(container) {
  container.className = 'mednos-verlauf';
  container.replaceChildren(ppHint('Lade Verlauf…'));

  let punkte = [];
  let dimension = 'app';
  let zeitraum = 'monat';
  let tabelleOffen = false;

  // Die Apps kommen als Linien aus den Daten, nicht aus einer festen Liste: seit die
  // Nutzerinnen eigene virtuelle Schluessel haben, gibt es neben `papers` und `study`
  // auch `user-FelixGunzer`. Eine feste Liste haette solche Posten stumm verschwinden
  // lassen — genau den Verbrauch, dessentwegen das Panel gebaut wurde.
  function male() {
    const z = ZEITRAEUME.find((e) => e.id === zeitraum);
    const serien = baueSerien(punkte, { korn: z.korn, maxGruppen: z.gruppen, dimension });

    const summe = document.createElement('span');
    summe.className = 'mednos-verlauf__summe';
    const b = document.createElement('b');
    b.textContent = usd(serien.summe);
    summe.append(b, document.createTextNode(' im Zeitraum'));

    const reiheArt = document.createElement('div');
    reiheArt.className = 'mednos-verlauf__reihe';
    reiheArt.append(knopfLeiste(DIMENSIONEN, dimension, (id) => { dimension = id; male(); }), summe);

    const reiheZeit = document.createElement('div');
    reiheZeit.className = 'mednos-verlauf__reihe';
    reiheZeit.append(knopfLeiste(ZEITRAEUME, zeitraum, (id) => { zeitraum = id; male(); }));

    const leiste = document.createElement('div');
    leiste.className = 'mednos-verlauf__leiste';
    leiste.append(reiheArt, reiheZeit);

    const teile = [leiste];

    if (!serien.gruppen.length) {
      // Radiology ist der Regelfall dafuer: die App laeuft nicht ueber den Proxy.
      teile.push(ppHint('Für diesen Zeitraum sind keine Kosten erfasst.'));
      container.replaceChildren(...teile);
      return;
    }

    const flaeche = document.createElement('div');
    flaeche.className = 'mednos-verlauf__flaeche';
    const tipp = document.createElement('div');
    tipp.className = 'mednos-verlauf__tipp';
    zeichne(flaeche, serien, tipp);

    const legende = document.createElement('div');
    legende.className = 'mednos-verlauf__legende';
    zeichneLegende(legende, serien.reihen);

    const aufklapp = document.createElement('button');
    aufklapp.type = 'button';
    aufklapp.className = 'mednos-verlauf__aufklapp';
    aufklapp.textContent = tabelleOffen ? 'Tabelle ausblenden' : 'Als Tabelle';
    const tabelle = document.createElement('div');
    if (tabelleOffen) zeichneTabelle(tabelle, serien);
    aufklapp.addEventListener('click', () => { tabelleOffen = !tabelleOffen; male(); });

    teile.push(flaeche, legende, aufklapp, tabelle);
    container.replaceChildren(...teile);
  }

  authApi('/api/admin/llm-verlauf').then((r) => {
    if (r.status !== 200 || !Array.isArray(r.body?.punkte)) {
      container.replaceChildren(ppHint(
        r.status === 401 || r.status === 403
          ? 'Keine Admin-Berechtigung (mehr).'
          : 'Verlauf derzeit nicht erreichbar.',
      ));
      return;
    }
    punkte = r.body.punkte;
    male();
  }).catch(() => {
    container.replaceChildren(ppHint('Verlauf derzeit nicht erreichbar.'));
  });
}
